/**
 * Notifications (`/notifications`) and the top-bar bell — docs/08 §2.2, docs/07 §9.
 *
 * In-app rows only: there is no email in v1 ([01 §5](../../../../docs/01-architecture.md)),
 * so this list is the whole notification channel and it has to be readable
 * without one. Each row is rendered from its `kind` + `payload`, and the payload
 * is what makes it a link rather than an announcement.
 *
 * Governance events with no notification kind of their own (settings changes,
 * seat vacations, the end of bootstrap) deliberately do not borrow a neighbouring
 * kind — they are surfaced through the audit log (docs/07 §9).
 */

import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Notification } from '@famtree/shared';
import { notificationsApi } from '../../api/client.js';
import { problemMessage } from '../../ui/problem.js';
import ui from '../../ui/components.module.css';
import styles from './notifications.module.css';

/** The bell's unread count, polled gently — it is a badge, not a live feed. */
export function useUnreadCount(): number {
  const query = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => notificationsApi.list({ unread: 'true', limit: 1 }),
    refetchInterval: 60_000,
  });
  return query.data?.unread_count ?? 0;
}

export function NotificationsPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const notifications = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => notificationsApi.list({ limit: 50 }),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['notifications'] });

  const markAll = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: refresh,
  });
  const markOne = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: refresh,
  });

  if (notifications.isPending) return <p className={ui.muted}>{t('app.loading')}</p>;
  if (notifications.isError) {
    return <p className={ui.problem}>{problemMessage(notifications.error, t)}</p>;
  }

  const rows = notifications.data.data;
  const unread = notifications.data.unread_count;

  return (
    <div>
      <div className={styles.header}>
        <h1>{t('notifications.title')}</h1>
        <span className={styles.spacer} />
        {unread > 0 && (
          <button
            className={ui.buttonSecondary}
            type="button"
            disabled={markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            {t('notifications.markAllRead', { count: unread })}
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <p className={ui.muted}>{t('notifications.empty')}</p>
      ) : (
        <ul className={styles.list}>
          {rows.map((row) => (
            <NotificationRow
              key={row.id}
              notification={row}
              onRead={() => markOne.mutate(row.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function NotificationRow({
  notification,
  onRead,
}: {
  notification: Notification;
  onRead: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const unread = notification.read_at == null;
  const target = linkFor(notification);

  const body = (
    <>
      <span className={styles.rowTitle}>{describe(notification, t)}</span>
      <span className={`${ui.muted} tabular`}>{formatWhen(notification.created_at)}</span>
    </>
  );

  return (
    <li className={`${styles.row} ${unread ? styles.rowUnread : ''}`}>
      {target ? (
        <Link className={styles.rowLink} to={target} onClick={unread ? onRead : undefined}>
          {body}
        </Link>
      ) : (
        <span className={styles.rowLink}>{body}</span>
      )}
      {unread && (
        <button className={styles.markRead} type="button" onClick={onRead}>
          {t('notifications.markRead')}
        </button>
      )}
    </li>
  );
}

/** Where the row takes you. A notification you cannot act on is half a feature. */
function linkFor(notification: Notification): string | null {
  const proposalId = notification.payload.proposal_id;
  if (typeof proposalId === 'string') return `/proposals/${proposalId}`;
  if (notification.kind === 'seat_granted') return '/proposals?tab=needs_my_vote';
  return null;
}

function describe(notification: Notification, t: TFunction): string {
  const summary =
    typeof notification.payload.summary === 'string' ? notification.payload.summary : '';
  switch (notification.kind) {
    case 'proposal_opened':
      return t('notifications.kinds.proposal_opened', { summary });
    case 'proposal_decided':
      return t('notifications.kinds.proposal_decided', {
        summary,
        status: t(`enums.proposal_status.${String(notification.payload.status)}`),
      });
    case 'vote_needed':
      return t('notifications.kinds.vote_needed', { summary });
    case 'seat_granted':
      return t('notifications.kinds.seat_granted', { seat: String(notification.payload.seat_no) });
    default:
      return t('notifications.kinds.photo_tagged');
  }
}

function formatWhen(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
